/// <amd-module name="VSS/Features/Services/OrganizationsService" />
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";

/**
 * Information about an organization (ProjectCollection) returned from the OrganizationsService
 */
export interface IOrganization {
    /**
     * Identifier for the org
     */
    id: string;

    /**
     * Name of the org
     */
    name: string;

    /**
     * URL to browse to the org
     */
    url: string;
}

/**
 * Data provider results from the my-oganizations data provider
 */
interface IMyOrganizationsData {
    /**
     * Organizations that the current user is a member of
     */
    organizations: IOrganization[];

    /**
     * Guids for most recently accessed hosts
     */
    mostRecentlyAccessedHosts: string[];

    /**
     * Url for creating a new account
     */
    createNewOrgUrl: string;

    /**
     * If the current org is not in the users orgs lists this will be true
     * and we have to queueu sync user account mapping
     */
    isUserAccountMappingRequired: Boolean;
}

/**
 * Service for working with organizations
 */
export interface IOrganizationsService extends IVssService {
    /**
     * Get all organizations that the current user is a member of
     */
    getUserOrganizations(): Promise<IOrganization[]>;

    /**
     * Get most recently used organizations for current user
     */
    getMostRecentlyAccessedUserOrganizations(): Promise<IOrganization[]>;

    /**
     * Gets the URL to navigate to for creating a new organization
     */
    getNewOrganizationUrl(): Promise<string | undefined>;

    /**
     * Gets if we have to queue Sync user accoung mapping
     */
    isUserAccountMappingRequired(): Promise<Boolean>;
}

class OrganizationsService extends VssService implements IOrganizationsService {
    private myOrganizationData: Promise<IMyOrganizationsData | undefined>;
    private myOrganizations: Promise<IOrganization[]>;
    private myRecentlyUsedOrganizations: Promise<IOrganization[]>;

    private getMyOrganizationData(): Promise<IMyOrganizationsData | undefined> {
        if (!this.myOrganizationData) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.myOrganizationData = contributionService.getDataAsync<IMyOrganizationsData>("ms.vss-features.my-organizations-data-provider");
        }
        return this.myOrganizationData;
    }

    public getUserOrganizations(): Promise<IOrganization[]> {
        if (!this.myOrganizations) {
            this.myOrganizations = this.getMyOrganizationData().then(data => {
                let organizations: IOrganization[] = [];
                if (data && data.organizations) {
                    organizations = [...data.organizations];
                    organizations.sort((org1, org2) => {
                        return localeIgnoreCaseComparer(org1.name, org2.name);
                    });
                }
                return organizations;
            });
        }
        return this.myOrganizations;
    }

    public getMostRecentlyAccessedUserOrganizations(): Promise<IOrganization[]> {
        if (!this.myRecentlyUsedOrganizations) {
            this.myRecentlyUsedOrganizations = this.getMyOrganizationData().then(data => {
                let organizations: IOrganization[] = [];
                if (data && data.mostRecentlyAccessedHosts && data.organizations) {
                    const organizationMapping: { [organizationId: string]: IOrganization } = {};
                    for (let org of data.organizations) {
                        organizationMapping[org.id] = org;
                    }

                    for (let hostid of data.mostRecentlyAccessedHosts) {
                        const organization = organizationMapping[hostid];
                        if (organization) {
                            organizations.push(organization);
                        }
                    }
                }
                return organizations;
            });
        }
        return this.myRecentlyUsedOrganizations;
    }

    public getNewOrganizationUrl(): Promise<string | undefined> {
        return this.getMyOrganizationData().then(data => {
            return data ? data.createNewOrgUrl : undefined;
        });
    }

    public isUserAccountMappingRequired(): Promise<Boolean> {
        return this.getMyOrganizationData().then(data => {
            return data ? data.isUserAccountMappingRequired : false;
        });
    }
}

Services.add("IOrganizationsService", { serviceFactory: OrganizationsService });

/**
 * Get all organizations that the current user is a member of
 */
export function getUserOrganizations(pageContext: IVssPageContext): Promise<IOrganization[]> {
    const orgService = pageContext.getService<IOrganizationsService>("IOrganizationsService");
    return orgService.getUserOrganizations();
}

/**
 * Gets the URL to navigate to for creating a new organization
 */
export function getNewOrganizationUrl(pageContext: IVssPageContext): Promise<string | undefined> {
    const orgService = pageContext.getService<IOrganizationsService>("IOrganizationsService");
    return orgService.getNewOrganizationUrl();
}

/**
 *  Get most recently used organizations for current user
 */
export function getMostRecentlyAccessedUserOrganizations(pageContext: IVssPageContext): Promise<IOrganization[]> {
    const orgService = pageContext.getService<IOrganizationsService>("IOrganizationsService");
    return orgService.getMostRecentlyAccessedUserOrganizations();
}

export function isUserAccountMappingRequired(pageContext: IVssPageContext): Promise<Boolean> {
    const orgService = pageContext.getService<IOrganizationsService>("IOrganizationsService");
    return orgService.isUserAccountMappingRequired();
}
